import React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import { format, parseISO } from "date-fns";
import { cn } from "@/lib/utils";

// Detect if device is mobile/tablet
const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;
  return /iPad|iPhone|iPod|Android/i.test(navigator.userAgent) ||
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
         window.innerWidth < 768;
};

/**
 * Mobile-friendly date input that uses native date picker on mobile
 * and Shadcn Calendar popover on desktop
 */
export const MobileDateInput = ({ 
  value, 
  onChange, 
  placeholder = "Select date",
  className,
  disabled = false,
  ...props 
}) => {
  const isMobile = isMobileDevice();
  
  // Convert Date object to string for native input
  const dateString = value ? format(value, "yyyy-MM-dd") : "";
  
  // Handle native date input change
  const handleNativeChange = (e) => {
    const dateValue = e.target.value;
    if (dateValue) {
      onChange(parseISO(dateValue));
    } else {
      onChange(null);
    }
  };

  // Mobile: Use native date input
  if (isMobile) {
    return (
      <Input
        type="date"
        value={dateString}
        onChange={handleNativeChange}
        disabled={disabled}
        className={cn("w-full h-11", className)}
        {...props}
      />
    );
  }

  // Desktop: Use Shadcn Calendar popover
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal h-10",
            !value && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? format(value, "PPP") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={onChange}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

export default MobileDateInput;
